import { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../data/site";
import { api } from "../lib/api";
import { useSiteImages } from "../lib/useSiteImages";

const STATUS_STYLES = {
  販売中: "bg-accent text-ink",
  開発中: "bg-white text-ink",
  管理中: "border border-accent text-accent",
  応相談: "bg-steel text-gray-300",
};

const PROCESS = [
  { num: "01", title: "管理", desc: "アパート・マンション・店舗・事業用物件の賃貸管理と維持管理。" },
  { num: "02", title: "取得", desc: "土地・物件の取得。建設会社の目線でポテンシャルを見極める。" },
  { num: "03", title: "活用", desc: "遊休地の活用提案。太陽光・資材置場・賃貸など最適解を企画。" },
  { num: "04", title: "開発", desc: "自社開発案件の推進。造成から建築、管理まで一貫して担う。" },
];

export default function RealEstate() {
  const siteImages = useSiteImages();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/properties")
      .then((r) => setProperties(r.data))
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="real-estate-page">
      <PageHero
        eyebrow="REAL ESTATE DIVISION"
        titleEn="REAL ESTATE"
        titleJa="不動産管理・開発事業"
        desc="建設会社だからできる不動産。施工・維持管理のノウハウを活かし、土地と建物の価値を高め続けます。"
        image={siteImages.real_estate || IMAGES.realestate}
      />

      <section className="border-t border-line py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="PROCESS" title="管理・取得・活用・開発" sub="不動産のライフサイクルを4つのフェーズで一貫サポートします。" />
          <div className="mt-16 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08} className="bg-ink">
                <div data-testid={`process-step-${p.num}`} className="h-full p-8 transition-colors hover:bg-coal">
                  <p className="font-display text-4xl font-extrabold text-accent">{p.num}</p>
                  <h3 className="mt-6 font-mincho text-xl font-bold text-white">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-400">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-coal py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="PROPERTIES" title="物件・開発案件" sub="現在取り扱い中の管理物件・開発案件・土地活用情報です。" />
          {loading ? (
            <p data-testid="properties-loading" className="mt-16 font-mono text-sm tracking-[0.3em] text-gray-500">
              LOADING...
            </p>
          ) : properties.length === 0 ? (
            <p data-testid="properties-empty" className="mt-16 text-sm text-gray-500">
              公開中の物件情報はまだありません。
            </p>
          ) : (
            <div data-testid="properties-grid" className="mt-16 grid gap-6 sm:grid-cols-2">
              {properties.map((p, i) => (
                <Reveal key={p.id} delay={(i % 2) * 0.08}>
                  <article data-testid={`property-card-${p.id}`} className="overflow-hidden border border-line bg-panel">
                    <div className="relative overflow-hidden">
                      <img src={p.image_url || IMAGES.realestate} alt={p.title} loading="lazy" className="aspect-[16/9] w-full object-cover transition-transform duration-700 hover:scale-105" />
                      <span className={`absolute left-4 top-4 px-3 py-1 font-mono text-[10px] font-bold tracking-[0.2em] ${STATUS_STYLES[p.status] || STATUS_STYLES["応相談"]}`}>
                        {p.status}
                      </span>
                    </div>
                    <div className="p-6">
                      <p className="font-mono text-[10px] tracking-[0.3em] text-accent">{p.kind}</p>
                      <h3 className="mt-2 font-mincho text-xl font-bold text-white">{p.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-gray-400">{p.description}</p>
                      <dl className="mt-5 space-y-2 border-t border-line pt-4 text-xs">
                        <div className="flex justify-between gap-4">
                          <dt className="text-gray-500">所在地</dt>
                          <dd className="text-right text-gray-300">{p.location}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-gray-500">面積</dt>
                          <dd className="text-right text-gray-300">{p.area}</dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-gray-500">価格・賃料</dt>
                          <dd className="text-right font-bold text-accent">{p.price}</dd>
                        </div>
                      </dl>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}
          <p className="mt-12 font-mono text-[10px] tracking-[0.2em] text-gray-600">※ 掲載物件・写真はサンプルです。正式な物件情報は順次公開予定です。</p>
        </div>
      </section>

      <CtaSection
        title="土地活用・物件管理のご相談は、"
        highlight="不動産部門へ。"
        text="遊休地の活用、賃貸管理の見直し、開発案件のご相談を承ります。"
        button="不動産の相談をする"
        to="/contact?type=realestate"
      />
    </div>
  );
}
